import { initializePermissionApi, PermissionApi } from '@data-delivery/permission-ui-api';
import { queryOptions } from '@tanstack/react-query';

const sftpUsersApi = initializePermissionApi(PermissionApi.SFTPUsersApi);

export const sftpUsersKeys = {
  all: () => ['sftp-users'] as const,

  visible: () =>
    queryOptions({
      queryKey: [...sftpUsersKeys.all(), 'visible'] as const,
      async queryFn() {
        const { data } = await sftpUsersApi.listVisibleUsers();
        return data;
      },
    }),

  byBusinessGroup: (id: string) => [...sftpUsersKeys.all(), 'by-bg', id] as const,
  byBusinessGroupQuery: (id: string) =>
    queryOptions({
      queryKey: sftpUsersKeys.byBusinessGroup(id),
      async queryFn() {
        const { data } = await sftpUsersApi.getAllUsersByBusinessGroupId({ id });
        return data;
      },
    }),

  byAssignment: (assignmentId: string) =>
    [...sftpUsersKeys.all(), 'by-assignment', assignmentId] as const,
  byAssignmentQuery: (assignmentId: string) =>
    queryOptions({
      queryKey: sftpUsersKeys.byAssignment(assignmentId),
      async queryFn() {
        const { data } = await sftpUsersApi.getAllUsersByAssignmentId({ assignmentId });
        return data;
      },
    }),

  byId: (id: string) => [...sftpUsersKeys.all(), 'by-id', id] as const,
  byIdQuery: (id?: string) =>
    queryOptions({
      queryKey: sftpUsersKeys.byId(id || 'none'),
      enabled: Boolean(id),
      async queryFn() {
        if (!id) {
          return undefined;
        }

        const { data } = await sftpUsersApi.getUserById({ userId: id });
        return data;
      },
    }),

  byLoginId: (loginId: string) => [...sftpUsersKeys.all(), 'by-login', loginId] as const,
  byLoginIdQuery: (loginId: string) =>
    queryOptions({
      queryKey: sftpUsersKeys.byLoginId(loginId),
      async queryFn() {
        const { data } = await sftpUsersApi.getUserByLoginId({ loginId });
        return data;
      },
    }),

  searchByLoginId: (searchId: string) =>
    [...sftpUsersKeys.all(), 'search-login', searchId] as const,
  searchByLoginIdQuery: (searchId: string) =>
    queryOptions({
      queryKey: sftpUsersKeys.searchByLoginId(searchId),
      async queryFn() {
        const { data } = await sftpUsersApi.searchUserByLoginId({ searchId });
        return data;
      },
    }),

  assignments: (userId: string) => [...sftpUsersKeys.all(), 'assignments', userId] as const,
  assignmentsQuery: (userId: string) =>
    queryOptions({
      queryKey: sftpUsersKeys.assignments(userId),
      async queryFn() {
        const { data } = await sftpUsersApi.getAssignmentsByUserId({ userId });
        return data;
      },
    }),

  sshKeys: (userId: string) => [...sftpUsersKeys.all(), 'sshkeys', userId] as const,
  sshKeysQuery: (userId: string) =>
    queryOptions({
      queryKey: sftpUsersKeys.sshKeys(userId),
      async queryFn() {
        const { data } = await sftpUsersApi.getSSHKeysByUserId({ userId });
        return data;
      },
    }),
};
